import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { useContext, useEffect } from 'react'

import {
  AnalyticsEvent,
  DistanceMetrics,
  RoutesPath,
  StorePersistKeys,
} from 'src/types'
import { useLocales } from 'src/contexts'
import { EngineTypes } from 'src/constants'

import { useNavigation } from 'src/hooks/useNavigation'
import { useManageUserCars } from 'src/hooks/useManageUserCars'
import { useManageFuelHistory } from 'src/hooks/useManageFuelHistory'
import { useAppSelector } from 'src/hooks/useAppSelector'
import { transformDataCarDates } from 'src/helpers/transformDataCarDates'
import { getFormattedTitle } from 'src/helpers/getFromatedTittle'
import { getFormattedDate } from 'src/helpers/getFormattedDate'
import { AnalyticsContext } from 'src/contexts/AnalyticsContext'

import { FromFuelTracking } from 'src/pages/FuelTracking/types'
import {
  getActiveCar,
  getFuelTypeOptions,
  getSimpleFields,
  getTotalCost,
} from 'src/pages/FuelTracking/helpers'
import { FuelType } from 'src/pages/FuelTracking/enums'
import { DEFAULT_VALUES_FUEL_STATE } from 'src/pages/FuelTracking/constants'
import { queryClient } from 'src/features/AppProviders/constants'
import { FuelTrackProps } from 'src/api/FuelTracking/types'

export const useFuelTracking = () => {
  const { translations } = useLocales()
  const { navigateTo } = useNavigation()

  const { currency, distanceMetric, activeCar } = useAppSelector(
    state => state.user,
  )
  const { logEvent } = useContext(AnalyticsContext)

  const { addFuelHistory, isLoadingAddFuelHistory } = useManageFuelHistory()
  const { userCars, updateCar, isLoadingGeneral } = useManageUserCars()

  const currentCar = userCars?.find(car => car.isActive)
  const isElectricCar = currentCar?.engineType === EngineTypes.Electric

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
    getValues,
  } = useForm<FromFuelTracking>({
    defaultValues: { ...DEFAULT_VALUES_FUEL_STATE },
  })

  const pricePerLiter = watch('pricePerLiter')
  const fuelQuantity = watch('fuelQuantity')

  const createFuelHistoryItem = (): FuelTrackProps => {
    const {
      fuelQuantity,
      totalCost,
      pricePerLiter,
      date,
      odometer,
      address,
      fuelType,
    } = getValues()

    return {
      fuelQuantity: Number(fuelQuantity) || 0,
      totalCost: Number(totalCost) || 0,
      fuelType: fuelType || FuelType.PETROL95,
      pricePerLiter: Number(pricePerLiter) || 0,
      address: address.trim() || '',
      odometer: Number(odometer) || 0,
      dateOfRefuel: getFormattedDate(date),
      currency,
      distanceUnit: distanceMetric,
    }
  }

  const updateCarMileage = (
    mileage: number,
    distanceUnit: DistanceMetrics,
  ): void => {
    const userActiveCar = getActiveCar(userCars)

    if (!userActiveCar) return

    const car = transformDataCarDates(userActiveCar)
    updateCar(
      {
        carNumber: activeCar,
        newData: { ...car, mileage, distanceMetric: distanceUnit },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [StorePersistKeys.USER_CARS],
          })
        },
      },
    )
  }

  const onSubmit = (): void => {
    const newItemHistory = createFuelHistoryItem()

    addFuelHistory(
      { payload: newItemHistory },
      {
        onSuccess: () => {
          reset(DEFAULT_VALUES_FUEL_STATE)
          logEvent(AnalyticsEvent.ADD_FUEL_ITEM)
          toast(translations.trackFuel.addInfoFuel.toaster, {
            description: translations.trackFuel.addInfoFuel.toasterBody,
            action: {
              label: 'Okay',
              onClick: () => toast.dismiss(),
            },
          })

          queryClient.invalidateQueries({
            queryKey: [StorePersistKeys.FUEL_HISTORY],
          })

          updateCarMileage(
            newItemHistory.odometer,
            newItemHistory.distanceUnit as DistanceMetrics,
          )
        },
      },
    )
  }

  const formattedTitle = (name: string, title: string): string => {
    return getFormattedTitle(name, title, currency, distanceMetric)
  }

  const gotMyCars = (): void => {
    navigateTo(RoutesPath.MY_CARS)
  }

  useEffect(() => {
    const totalCost = getTotalCost(pricePerLiter, fuelQuantity)

    setValue('totalCost', totalCost.toString())
  }, [pricePerLiter, fuelQuantity])

  const breadcrumbItems = [
    { labelKey: translations.breadcrumbItems.home, href: '/' },
    {
      labelKey: translations.breadcrumbItems.fuelTracking,
      href: '/fuel',
    },
    { labelKey: translations.breadcrumbItems.add },
  ]

  useEffect(() => {
    if (!isElectricCar) return

    const fuelType = isElectricCar ? FuelType.Electricity : FuelType.PETROL95

    setValue('fuelType', fuelType)
  }, [isElectricCar])

  const isElectricType = watch('fuelType') === FuelType.Electricity

  return {
    locale: translations.trackFuel.addInfoFuel,
    breadcrumbItems,
    handleSubmit,
    onSubmit,
    control,
    setValue,
    errors,
    isLoadingAddFuelHistory: isLoadingAddFuelHistory || isLoadingGeneral,
    formattedTitle,
    gotMyCars,
    fields: getSimpleFields(
      translations.trackFuel,
      true,
      isElectricCar,
      isElectricType,
    ),
    fuelTypeOptions: getFuelTypeOptions(translations.trackFuel),
    activeCar,
  }
}
